import { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import { Box } from "@mui/material";

const IndividualTrackerRow = ({
  data,
  selectedTt = [],
  selectedPier = [],
  pierlegends,
  view,
  current,
  type,
  active,
  handleClickTorquetube,
  handleClickPier

}) => {
  const containerRef = useRef(null);

  const renderMap = useCallback(() => {
    const svg = d3.select(containerRef.current);
    svg.selectAll("*").remove();

    const spacing = 34;
    const yCenter = 30;
    const totalTt = data?.tt?.length || 0;
    const contentWidth = totalTt * spacing + 50;

    svg.attr("viewBox", `0 0 ${contentWidth} 60`);
    svg.attr("preserveAspectRatio", "xMidYMid meet");

    // Highlight selected TT
    if(selectedTt?.length) {
      selectedTt?.forEach((tt) => {
        svg.append("rect")
          .attr("x", tt?.tt_part_order * spacing + 10)
          .attr("y", 24)
          .attr("width", 35)
          .attr("height", 12)
          .attr("fill", type === "qc" ? "#F6BE00" : "#06b706");
      });
    }
  

    // Highlight selected Pier
    if(selectedPier?.length) {
      selectedPier?.forEach((pr) => {
        svg.append("rect")
          .attr("x", pr?.pier_part_order * spacing - 2)
          .attr("y", 18)
          .attr("width", 25)
          .attr("height", 25)
          .attr("fill", type === "qc" ? "#F6BE00" : "#06b706");
      });
    }
   

    // Draw TT parts
    data?.tt?.forEach((key) => {
      const { tracker_row_type, tt_part_order } = key ?? {};
      const x = tt_part_order * spacing;

      if (tracker_row_type === 2) {
        svg.append("line")
          .attr("x1", x + 15)
          .attr("y1", yCenter)
          .attr("x2", x + 39)
          .attr("y2", yCenter)
          .attr("stroke-width", "5px")
          .attr("stroke-dasharray", "1 1")
          .attr("stroke", "gray")
          .attr("cursor", "pointer")
          .on("click", () => handleClickTorquetube(key));
      } else {
        const fill = tracker_row_type === 1 ? "white" : "gray";
        svg.append("rect")
          .attr("x", x + 10)
          .attr("y", yCenter - 2.5)
          .attr("width", 28)
          .attr("height", "5px")
          .attr("fill", fill)
          .attr("stroke", "gray")
          .attr("stroke-width", "1px")
          .attr("cursor", "pointer")
          .on("click", () => handleClickTorquetube(key));
      }
    });

    // Draw Piers
    svg.selectAll("g.pier")
      .data(data?.piers || [], d => d.id)
      .join(
        (enter) => {
          const g = enter.append("g")
            .attr("class", (d) => `pier trow-${d.id}-${d.pier_type_id}`)
            .attr("id", (d) => `pier-${d.id}-${d.pier_type_id}`)
            .style("cursor", "pointer");

          g.each(function (pr) {
            const group = d3.select(this);
            const partNum = parseInt(pr.pier_part_number);
            const legend = pierlegends?.[partNum];
            const x = pr.pier_part_order * spacing;

            const shape = legend?.shape || "Circle";
            const stroke = legend?.border || "black";
            const fill = legend?.background || "white";
            const strokeWidth = legend?.width || 1;

            if (shape === "Square") {
              group.append("rect")
                .attr("x", x + 3)
                .attr("y", yCenter - 7)
                .attr("width", 14)
                .attr("height", 14)
                .attr("stroke", stroke)
                .attr("fill", fill)
                .attr("stroke-width", strokeWidth)
                .on("click", () => handleClickPier(pr));
            } else if (shape === "Diamond") {
              group.append("rect")
                .attr("x", x + 3)
                .attr("y", yCenter - 7)
                .attr("width", 14)
                .attr("height", 14)
                .attr("stroke", stroke)
                .attr("fill", fill)
                .attr("stroke-width", strokeWidth)
                .attr("transform", `translate(${x + 10}, ${yCenter}) rotate(45) translate(-${x + 10}, -${yCenter})`)
                .on("click", () => handleClickPier(pr));
            } else {
              group.append("circle")
                .attr("cx", x + 10)
                .attr("cy", yCenter)
                .attr("r", 7)
                .attr("stroke", stroke)
                .attr("fill", fill)
                .attr("stroke-width", strokeWidth)
                .on("click", () => handleClickPier(pr));
            }

            group.append("text")
              .attr("x", x + 10)
              .attr("y", yCenter + 1)
              .attr("text-anchor", "middle")
              .attr("alignment-baseline", "middle")
              .attr("font-size", "12px")
              .attr("fill", "#000")
              .text("-")
              .style("cursor", "pointer")
              .on("click", () => handleClickPier(pr));
          });

          return g;
        },
        (update) => update,
        (exit) => exit.remove()
      );
      // eslint-disable-next-line
  }, [data, selectedTt, selectedPier, type, pierlegends]);

  useEffect(() => {
    renderMap();
  }, [data, renderMap]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100%",
        minHeight: "50px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          overflowX: "auto",
          height: "50px",
        }}
      >
        <svg ref={containerRef} style={{ width: "100%", height: "100%" }} />
      </Box>
    </Box>
  );
};

export default IndividualTrackerRow;
